import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import config from '../../../config/config';
import { formatPageTitle, resolveAbsoluteUrl, resolveSiteOrigin } from './seoHelpers';

const DEFAULT_DESCRIPTION =
  'Find trusted plumbing, HVAC, electrical, and landscaping pros in Northern Kentucky. NKY Connect matches you with local service providers.';

const DEFAULT_OG_IMAGE_PATH = '/logo.png';

/**
 * Shared page metadata for SEO and social previews.
 *
 * @param {object} props
 * @param {string} [props.title] — Page title segment or full title (suffix added if missing)
 * @param {string} [props.description]
 * @param {string} [props.path] — Path for canonical (defaults to current location)
 * @param {string} [props.canonicalUrl] — Absolute canonical URL (overrides path)
 * @param {string} [props.image] — Absolute or site-relative image URL
 * @param {boolean} [props.noindex]
 * @param {string} [props.type] — og:type (default website)
 */
const SEO = ({
  title,
  description = DEFAULT_DESCRIPTION,
  path,
  canonicalUrl,
  image,
  noindex = false,
  type = 'website',
}) => {
  const location = useLocation();
  const origin = resolveSiteOrigin(config.siteUrl);
  const pageTitle = formatPageTitle(title);
  const canonical =
    canonicalUrl ||
    resolveAbsoluteUrl(origin, path != null ? path : location.pathname);
  const ogImage = resolveAbsoluteUrl(
    origin,
    image || DEFAULT_OG_IMAGE_PATH,
  );

  // CRA shell meta tags lack data-rh, so Helmet would otherwise leave duplicates.
  useEffect(() => {
    document
      .querySelectorAll('meta[name="description"]:not([data-rh])')
      .forEach((el) => el.remove());
  }, []);

  return (
    <Helmet prioritizeSeoTags>
      <title>{pageTitle}</title>
      <meta name="description" content={description} />
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow" />
      )}
      <link rel="canonical" href={canonical} />

      <meta property="og:site_name" content="NKY Connect" />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
    </Helmet>
  );
};

export default SEO;
export { DEFAULT_DESCRIPTION, DEFAULT_OG_IMAGE_PATH };
